/**
 * ScoresEditor Component
 *
 * Admin editor for Scores lookup table
 * - Filter by ScoreCategory
 * - Table with all score dimension columns
 * - CRUD operations for score templates
 */

import {
  useAdminScores,
  useScoreCategories,
  useCreateScore,
  useUpdateScore,
  useDeleteScore,
  useReorderScore,
} from '@medsuite/api-client';
import { Button, Alert, Spinner, useDialog } from '@medsuite/ui-components';
import { useState, useEffect } from 'react';

import type { Score } from '@medsuite/models';

interface ScoresEditorProps {
  onDataLoaded?: (count: number) => void;
  initialCategoryId?: number;
}

export default function ScoresEditor({ onDataLoaded, initialCategoryId }: ScoresEditorProps) {
  const { data: scores = [], isLoading, error } = useAdminScores();
  const { data: scoreCategories = [] } = useScoreCategories();

  // Mutations
  const createMutation = useCreateScore();
  const updateMutation = useUpdateScore();
  const deleteMutation = useDeleteScore();
  const reorderMutation = useReorderScore();

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    initialCategoryId || null
  );
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newItem, setNewItem] = useState({
    score_name: '',
    score_category_id: selectedCategoryId || 0,
    security_score: 0,
    growth_score: 0,
    efficiency_score: 0,
    infrastructure_score: 0,
    clinical_score: 0,
    strategy_allocation: 0,
  });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingItem, setEditingItem] = useState<Partial<Score>>({});

  // Dialog hooks
  const { alert, confirm } = useDialog();

  // Notify parent of data count
  useEffect(() => {
    if (onDataLoaded && scores) {
      onDataLoaded(scores.length);
    }
  }, [scores, onDataLoaded]);

  // Filter scores by selected category and sort by display_sequence
  const filteredScores = (
    selectedCategoryId ? scores.filter((s) => s.score_category_id === selectedCategoryId) : scores
  ).sort((a, b) => (a.display_sequence || 0) - (b.display_sequence || 0));

  // Handle add
  const handleSaveAdd = async () => {
    if (!newItem.score_category_id || !newItem.score_name) {
      await alert({ title: 'Validation Error', message: 'Please fill in required fields' });
      return;
    }
    try {
      await createMutation.mutateAsync(newItem);
      setIsAddingNew(false);
      setNewItem({
        score_name: '',
        score_category_id: selectedCategoryId || 0,
        security_score: 0,
        growth_score: 0,
        efficiency_score: 0,
        infrastructure_score: 0,
        clinical_score: 0,
        strategy_allocation: 0,
      });
    } catch (error) {
      console.error('Failed to add score:', error);
    }
  };

  // Handle edit
  const handleSaveEdit = async () => {
    if (!editingId) {
      return;
    }
    try {
      await updateMutation.mutateAsync({ id: editingId, data: editingItem });
      setEditingId(null);
      setEditingItem({});
    } catch (error) {
      console.error('Failed to update score:', error);
    }
  };

  // Handle delete
  const handleDelete = async (id: number) => {
    const confirmed = await confirm({
      title: 'Delete Score',
      message: 'Are you sure you want to delete this score?',
      variant: 'danger',
      confirmText: 'Delete',
    });
    if (!confirmed) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(id);
    } catch (error) {
      console.error('Failed to delete score:', error);
    }
  };

  // Handle reorder
  const handleReorder = async (id: number, direction: 'up' | 'down') => {
    try {
      await reorderMutation.mutateAsync({ id, direction });
    } catch (error) {
      console.error('Failed to reorder score:', error);
    }
  };

  // Get category name by ID
  const getCategoryName = (categoryId: number) => {
    const category = scoreCategories.find((c) => c.score_category_id === categoryId);
    return category?.score_category_name || 'Unknown';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return <Alert variant="error">Failed to load scores: {error.message}</Alert>;
  }

  return (
    <div className="space-y-4">
      {/* Header with Filter and Add Button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Score Templates
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {filteredScores.length} score{filteredScores.length === 1 ? '' : 's'}
            {selectedCategoryId && ' in selected category'}
          </p>
        </div>
        {!isAddingNew && (
          <Button variant="primary" onClick={() => setIsAddingNew(true)}>
            Add New
          </Button>
        )}
      </div>

      {/* Category Filter */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Filter by Category:
        </label>
        <select
          value={selectedCategoryId || ''}
          onChange={(e) => setSelectedCategoryId(e.target.value ? Number(e.target.value) : null)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
        >
          <option value="">All Categories</option>
          {scoreCategories.map((category) => (
            <option key={category.score_category_id} value={category.score_category_id}>
              {category.score_category_name}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                ID
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Seq
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Score Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Category
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Security
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Growth
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Efficiency
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Infrastructure
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Clinical
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Allocation
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Order
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {/* Add New Row */}
            {isAddingNew && (
              <tr className="bg-blue-50 dark:bg-blue-950">
                <td className="px-4 py-3">
                  <span className="text-sm text-gray-500 dark:text-gray-400 italic">Auto</span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className="text-sm text-gray-500 dark:text-gray-400 italic">Auto</span>
                </td>
                <td className="px-4 py-3">
                  <input
                    type="text"
                    value={newItem.score_name}
                    onChange={(e) => setNewItem({ ...newItem, score_name: e.target.value })}
                    className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded text-sm"
                    placeholder="Score name"
                  />
                </td>
                <td className="px-4 py-3">
                  <select
                    value={newItem.score_category_id}
                    onChange={(e) =>
                      setNewItem({ ...newItem, score_category_id: Number(e.target.value) })
                    }
                    className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded text-sm"
                  >
                    <option value={0}>Select...</option>
                    {scoreCategories.map((cat) => (
                      <option key={cat.score_category_id} value={cat.score_category_id}>
                        {cat.score_category_name}
                      </option>
                    ))}
                  </select>
                </td>
                {[
                  'security_score',
                  'growth_score',
                  'efficiency_score',
                  'infrastructure_score',
                  'clinical_score',
                  'strategy_allocation',
                ].map((field) => (
                  <td key={field} className="px-4 py-3">
                    <input
                      type="number"
                      value={(newItem as unknown as Record<string, number>)[field]}
                      onChange={(e) => setNewItem({ ...newItem, [field]: Number(e.target.value) })}
                      className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded text-sm text-center"
                    />
                  </td>
                ))}
                <td className="px-4 py-3"></td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => setIsAddingNew(false)}
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 text-sm mr-2"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveAdd}
                    disabled={createMutation.isPending}
                    className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 text-sm"
                  >
                    {createMutation.isPending ? 'Saving...' : 'Save'}
                  </button>
                </td>
              </tr>
            )}

            {/* Data Rows */}
            {filteredScores.length === 0 && !isAddingNew ? (
              <tr>
                <td
                  colSpan={12}
                  className="px-4 py-12 text-center text-gray-500 dark:text-gray-400"
                >
                  {selectedCategoryId
                    ? 'No scores found for this category. Click "Add New" to create one.'
                    : 'No scores found. Click "Add New" to create one.'}
                </td>
              </tr>
            ) : (
              filteredScores.map((item) => {
                const isEditing = editingId === item.score_id;
                // Check if current item is in same category as neighbors for reorder enable
                const categoryScores = filteredScores.filter(
                  (s) => s.score_category_id === item.score_category_id
                );
                const categoryIndex = categoryScores.findIndex((s) => s.score_id === item.score_id);
                const canMoveUp = categoryIndex > 0;
                const canMoveDown = categoryIndex < categoryScores.length - 1;

                return (
                  <tr
                    key={item.score_id}
                    className={isEditing ? 'bg-yellow-50 dark:bg-yellow-950' : ''}
                  >
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                      {item.score_id}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                      {item.display_sequence || 0}
                    </td>
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editingItem.score_name || ''}
                          onChange={(e) =>
                            setEditingItem({ ...editingItem, score_name: e.target.value })
                          }
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded text-sm"
                        />
                      ) : (
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {item.score_name}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <select
                          value={editingItem.score_category_id || 0}
                          onChange={(e) =>
                            setEditingItem({
                              ...editingItem,
                              score_category_id: Number(e.target.value),
                            })
                          }
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded text-sm"
                        >
                          {scoreCategories.map((cat) => (
                            <option key={cat.score_category_id} value={cat.score_category_id}>
                              {cat.score_category_name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          {getCategoryName(item.score_category_id)}
                        </div>
                      )}
                    </td>
                    {[
                      'security_score',
                      'growth_score',
                      'efficiency_score',
                      'infrastructure_score',
                      'clinical_score',
                      'strategy_allocation',
                    ].map((field) => (
                      <td key={field} className="px-4 py-3">
                        {isEditing ? (
                          <input
                            type="number"
                            value={(editingItem as Record<string, number>)[field] ?? 0}
                            onChange={(e) =>
                              setEditingItem({ ...editingItem, [field]: Number(e.target.value) })
                            }
                            className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded text-sm text-center"
                          />
                        ) : (
                          <div className="text-sm text-gray-900 dark:text-gray-100 text-center">
                            {(item as unknown as Record<string, number>)[field]}
                          </div>
                        )}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleReorder(item.score_id, 'up')}
                          disabled={!canMoveUp || reorderMutation.isPending || isEditing}
                          className={`p-1 rounded ${
                            canMoveUp && !reorderMutation.isPending && !isEditing
                              ? 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
                              : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                          }`}
                          title="Move up"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleReorder(item.score_id, 'down')}
                          disabled={!canMoveDown || reorderMutation.isPending || isEditing}
                          className={`p-1 rounded ${
                            canMoveDown && !reorderMutation.isPending && !isEditing
                              ? 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
                              : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                          }`}
                          title="Move down"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => setEditingId(null)}
                            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 text-sm mr-2"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveEdit}
                            disabled={updateMutation.isPending}
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 text-sm"
                          >
                            {updateMutation.isPending ? 'Saving...' : 'Save'}
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => {
                              setEditingId(item.score_id);
                              setEditingItem(item);
                            }}
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 text-sm mr-2"
                            disabled={editingId !== null || isAddingNew}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(item.score_id)}
                            disabled={deleteMutation.isPending}
                            className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 text-sm"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
